import React from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";

import CustomText from "../../../components/common/general/CustomText/CustomText";
import { ArrowSquareOutIcon } from "@phosphor-icons/react";
import { UserChatMessageAnalyticsData } from "../../../graphql/generated/graphql";

interface UserChatMessageAnalyticsTableProps {
  users: UserChatMessageAnalyticsData[];
  onUserSelect: (userId: string) => void;
}

const UserChatMessageAnalyticsTable: React.FC<UserChatMessageAnalyticsTableProps> = ({ users, onUserSelect }) => {
  const handleViewUser = (userId: string) => {
    onUserSelect(userId);
  };

  const getStudentInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  return (
    <>
      <ScrollView style={styles.tableContainer} tabIndex={0}>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <View style={[styles.cell, styles.studentNameCell]}>
              <CustomText text="Student Name" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.messagesSentCell]}>
              <CustomText text="Messages Sent" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.aiResponsesCell]}>
              <CustomText text="AI Responses" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.totalMessagesCell]}>
              <CustomText text="Total Messages" size="s" weight="medium" style={styles.headerText} />
            </View>
          </View>

          {users.map((user) => (
            <View key={user.id} style={styles.tableRow}>
              <View style={[styles.cell, styles.studentNameCell]}>
                <View style={styles.studentNameContent}>
                  <View style={styles.studentAvatar}>
                    <CustomText
                      text={getStudentInitial(user.name)}
                      size="s"
                      weight="bold"
                      style={styles.avatarText}
                      useTranslationText={false}
                    />
                  </View>
                  <View style={styles.studentInfo}>
                    <TouchableOpacity style={styles.studentNameDetails} onPress={() => handleViewUser(user.id)}>
                      <CustomText
                        text={user.name}
                        weight="medium"
                        style={styles.studentName}
                        useTranslationText={false}
                      />
                      <ArrowSquareOutIcon size={16} color="#3E68FE" />
                    </TouchableOpacity>
                    <CustomText text={user.email} size="xs" style={styles.studentEmail} useTranslationText={false} />
                  </View>
                </View>
              </View>

              <View style={[styles.cell, styles.messagesSentCell]}>
                <CustomText
                  text={user.studentMessagesCount?.toString() || "0"}
                  size="s"
                  style={styles.numericText}
                  useTranslationText={false}
                />
              </View>

              <View style={[styles.cell, styles.aiResponsesCell]}>
                <CustomText
                  text={user.aiMessagesCount?.toString() || "0"}
                  size="s"
                  style={styles.numericText}
                  useTranslationText={false}
                />
              </View>

              <View style={[styles.cell, styles.totalMessagesCell]}>
                <CustomText
                  text={user.totalMessages?.toString() || "0"}
                  size="s"
                  style={styles.numericText}
                  useTranslationText={false}
                />
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  tableContainer: {
    flex: 1,
  },
  table: {
    backgroundColor: "#fff",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 72,
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  studentNameCell: {
    flex: 3,
  },
  messagesSentCell: {
    flex: 1.2,
    alignItems: "center",
  },
  aiResponsesCell: {
    flex: 1.2,
    alignItems: "center",
  },
  totalMessagesCell: {
    flex: 1.2,
    alignItems: "center",
  },
  headerText: {
    color: "#6B7280",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  studentNameContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  studentInfo: {
    flexDirection: "column",
    gap: 4,
  },
  studentNameDetails: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  studentAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#3E68FE",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#FFFFFF",
    fontSize: 14,
  },
  studentName: {
    color: "#3E68FE",
    fontSize: 15,
    cursor: "pointer",
  },
  studentEmail: {
    color: "#6B7280",
  },
  numericText: {
    color: "#374151",
    fontWeight: "500",
  },
});

export default UserChatMessageAnalyticsTable;
